import * as React from 'react';
import { omit } from 'rambda';

import { Refinements, RefinementType } from '@packages/common';

import { RefinementsContext } from '../RefinementsContext';
import { clearAll$, numericFilter$, facetFilter$ } from './refinementsEvents';

export type CurrentRefinementItem = {
  label: string;
  remove: VoidFunction;
};

export const useRefinements = () => {
  const { refinements, setRefinements } = React.useContext(RefinementsContext);
  const { facetFilters = {}, numericFilters = {} } = refinements ?? {};
  const clearAllRefinements = React.useMemo(() => {
    return () => {
      clearAll$.next();
      setRefinements((refinements) => ({
        ...refinements,
        facetFilters: {},
        numericFilters: {},
      }));
    };
  }, [setRefinements]);

  const updateFacetFilters = React.useMemo(
    () => (facetFilters: Refinements[RefinementType.FACET_FILTERS]) => {
      setRefinements((refinements) => ({
        ...refinements,
        facetFilters,
        page: 0,
      }));
    },
    [setRefinements],
  );

  const facetFilterRefinements = Object.keys(facetFilters).reduce((acc, facetFeatureMapping) => {
    const filterValues = facetFilters[facetFeatureMapping].values;
    const list = filterValues.map((filter) => {
      return {
        label: filter.name,
        remove: () => {
          updateFacetFilters({
            ...facetFilters,
            [facetFeatureMapping]: {
              ...facetFilters[facetFeatureMapping],
              values: facetFilters[facetFeatureMapping].values.filter((value) => value.seoName !== filter.seoName),
            },
          });

          facetFilter$.next({ isActive: false, facetSeoName: filter.seoName, valueSeoName: filter.featureValue });
        },
      };
    });

    return [...acc, ...list];
  }, [] as CurrentRefinementItem[]);

  const updateNumericFilters = React.useMemo(
    () => (numericFilters: Refinements[RefinementType.NUMERIC_FILTERS]) => {
      setRefinements((refinements) => ({
        ...refinements,
        numericFilters,
        page: 0,
      }));
    },
    [setRefinements],
  );

  const numericFilterRefinements = Object.keys(numericFilters).reduce((acc, facetFeatureMapping) => {
    const filter = numericFilters[facetFeatureMapping];
    return [
      ...acc,
      {
        label: `${filter.value.min} - ${filter.value.max} ${filter.unit}`,
        remove: () => {
          updateNumericFilters(omit([facetFeatureMapping], numericFilters));
          numericFilter$.next({ seoName: filter.seoName });
        },
      },
    ];
  }, [] as CurrentRefinementItem[]);

  return {
    currentRefinements: [...facetFilterRefinements, ...numericFilterRefinements],
    clearAllRefinements,
    updateFacetFilters,
    updateNumericFilters,
  };
};
